import { useSelector } from 'react-redux';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ExternalLink, 
  FileText, 
  Calendar, 
  Building2, 
  ChevronUp,
  ChevronDown,
  MapPin,
  Info
} from 'lucide-react';

const EdgarFilings = () => {
  const { t } = useTranslation();
  const companyInfo = useSelector(state => state.edgar.companyInfo);
  const filings = useSelector(state => state.edgar.filings);
  const loading = useSelector(state => state.edgar.loading);
  const error = useSelector(state => state.edgar.error);

  const [hoveredCard, setHoveredCard] = useState(null);
  const [selectedForm, setSelectedForm] = useState('all');
  const [showAll, setShowAll] = useState(false);
  const [expandedFilings, setExpandedFilings] = useState({});

  // Toggle individual filing expansion
  const toggleFiling = (accessionNumber) => {
    setExpandedFilings(prev => ({
      ...prev,
      [accessionNumber]: !prev[accessionNumber]
    }));
  };

  const hasData = Array.isArray(filings) && filings.length > 0;


  // Parse summary bullets
  const parseSummaryBullets = (summaryBullets) => {
    if (!summaryBullets) return [];

    // Case 1 — Already an array
    if (Array.isArray(summaryBullets)) {
      return summaryBullets
        .map(b => (typeof b === "string" ? b.trim() : ""))
        .filter(Boolean);
    }

    if (typeof summaryBullets !== "string") return [];

    let text = summaryBullets.replace(/\r/g, "").trim();
    if (!text) return [];

    /**
     * STEP 1 — Normalize:
     * Force each markdown header (**Header:**)
     * to start on its own line
     */
    text = text.replace(/(?<!\n)\s*(\*\*[^*]+\*\*:)/g, "\n$1");

    /**
     * STEP 2 — Split by lines
     */
    const lines = text
      .split("\n")
      .map(l => l.trim())
      .filter(Boolean);

    /**
     * STEP 3 — Extract header/body pairs
     */
    const bullets = [];

    for (const line of lines) {
      const match = line.match(/^\*\*([^*]+)\*\*:\s*(.+)/);

      if (match) {
        bullets.push(`**${match[1]}:** ${match[2]}`);
      } else {
        bullets.push(line);
      }
    }

    return bullets;
  };







  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '—';
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);
    
    if (diffInHours < 1) return t('edgar.justNow');
    if (diffInHours < 24) return t('edgar.hoursAgo', { count: diffInHours });
    if (diffInDays === 1) return t('edgar.yesterday');
    if (diffInDays < 30) return t('edgar.daysAgo', { count: diffInDays });
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  // Get form type configuration
  const getFormConfig = (formType) => {
    const form = formType?.toUpperCase() || '';
    
    // Critical forms (red theme)
    if (['8-K', '8-K/A'].includes(form)) {
      return {
        icon: <FileText className="w-4 h-4 text-rose-400" />,
        bgGradient: 'from-rose-500/10 via-red-500/5 to-transparent',
        borderColor: 'border-rose-500/30',
        textColor: 'text-rose-400',
        glowColor: 'shadow-rose-500/20',
        category: 'critical',
        label: t('edgar.formLabels.critical')
      };
    }
    
    // Quarterly/Annual reports (green theme)
    if (['10-Q', '10-K', '10-Q/A', '10-K/A'].includes(form)) {
      return {
        icon: <FileText className="w-4 h-4 text-emerald-400" />,
        bgGradient: 'from-emerald-500/10 via-green-500/5 to-transparent',
        borderColor: 'border-emerald-500/30',
        textColor: 'text-emerald-400',
        glowColor: 'shadow-emerald-500/20',
        category: 'reports',
        label: t('edgar.formLabels.reports')
      };
    }
    
    // Proxy statements and other governance (amber theme)
    if (['DEF 14A', 'DEFA14A', 'SC 13G', 'SC 13D', 'SCHEDULE 13G/A', 'SCHEDULE 13G', 'PX14A6G'].includes(form)) {
      return {
        icon: <Building2 className="w-4 h-4 text-amber-400" />,
        bgGradient: 'from-amber-500/10 via-yellow-500/5 to-transparent',
        borderColor: 'border-amber-500/30',
        textColor: 'text-amber-400',
        glowColor: 'shadow-amber-500/20',
        category: 'governance',
        label: t('edgar.formLabels.governance')
      };
    }

    // Form 4 and insider trading (purple theme)
    if (['4', '4/A'].includes(form)) {
      return {
        icon: <FileText className="w-4 h-4 text-purple-400" />,
        bgGradient: 'from-purple-500/10 via-violet-500/5 to-transparent',
        borderColor: 'border-purple-500/30',
        textColor: 'text-purple-400',
        glowColor: 'shadow-purple-500/20',
        category: 'insider',
        label: t('edgar.formLabels.insider')
      };
    }

    // S-8 and registration (teal theme)
    if (['S-8', 'S-8/A'].includes(form)) {
      return {
        icon: <FileText className="w-4 h-4 text-teal-400" />,
        bgGradient: 'from-teal-500/10 via-cyan-500/5 to-transparent',
        borderColor: 'border-teal-500/30',
        textColor: 'text-teal-400',
        glowColor: 'shadow-teal-500/20',
        category: 'registration',
        label: t('edgar.formLabels.registration')
      };
    }
    
    // Default (cyan theme)
    return {
      icon: <FileText className="w-4 h-4 text-cyan-400" />,
      bgGradient: 'from-cyan-500/10 via-blue-500/5 to-transparent',
      borderColor: 'border-cyan-500/30',
      textColor: 'text-cyan-400',
      glowColor: 'shadow-cyan-500/20',
      category: 'other',
      label: t('edgar.formLabels.other')
    };
  };

  // Transform and filter data
  const transformedData = useMemo(() => {
    if (!Array.isArray(filings)) return [];
    return filings.map((filing) => ({
      ...filing,
      formConfig: getFormConfig(filing.form)
    }));
  }, [filings]);

  const filteredData = useMemo(() => {
    const base = selectedForm === 'all' 
      ? transformedData
      : transformedData.filter((filing) => filing.formConfig.category === selectedForm);
    return [...base].sort((a, b) => new Date(b.filing_date) - new Date(a.filing_date));
  }, [transformedData, selectedForm]);

  // Visible slice (3 by default)
  const visibleFilings = useMemo(
    () => (showAll ? filteredData : filteredData.slice(0, 3)),
    [filteredData, showAll]
  );

  // Counts for pills
  const formCounts = useMemo(() => {
    const counts = transformedData.reduce((acc, filing) => {
      acc[filing.formConfig.category] = (acc[filing.formConfig.category] || 0) + 1;
      return acc;
    }, {});
    return {
      all: transformedData.length,
      critical: counts.critical || 0,
      reports: counts.reports || 0,
      governance: counts.governance || 0,
      insider: counts.insider || 0,
      registration: counts.registration || 0,
      other: counts.other || 0,
    };
  }, [transformedData]);

  const renderAddress = (type, addr) => (
    <div className="text-xs text-gray-300 space-y-1">
      <div className="flex items-center gap-2">
        <MapPin className="w-3 h-3 text-cyan-400" />
        <span className="text-cyan-400 font-medium">{type}</span>
      </div>
      <div className="text-gray-400 pl-5 leading-relaxed">
        {addr.street1}<br />
        {addr.city}, {addr.stateOrCountry} {addr.zipCode}
      </div>
    </div>
  );

  if (error && !hasData) {
    return (
      <div className="h-full flex flex-col bg-slate-900/50 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden shadow-2xl shadow-black/20">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4 p-8">
            <FileText className="w-10 h-10 text-rose-400 mx-auto" />
            <h3 className="text-lg font-semibold text-gray-200">
              {t('edgar.failedToLoad')}
            </h3>
            <p className="text-gray-400 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="h-full flex flex-col bg-slate-900/50 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden shadow-2xl shadow-black/20">
      {/* Header */}
      <div className="p-6 border-b border-white/10 bg-gradient-to-r from-white/5 via-white/10 to-transparent backdrop-blur-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent mb-1">
              {t('edgar.title')}
            </h2>
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <FileText  className="w-4 h-4" />
              <span>{t('edgar.subtitle')}</span>
              <span className="text-gray-600">•</span>
              <span className="text-blue-400 font-medium">{t('edgar.filingsCount', { count: filteredData.length })}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1 bg-white/5 backdrop-blur-sm rounded-full border border-white/10">
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse shadow-lg shadow-blue-400/50"></div>
            <span className="text-xs text-gray-300 font-medium">{t('edgar.officialBadge')}</span>
          </div>
        </div>

        {/* Company Info Card */}
        {companyInfo && (
          <div className="mb-6 bg-gradient-to-r from-white/10 via-white/5 to-transparent backdrop-blur-sm rounded-xl border border-white/10 p-4 space-y-4 shadow-lg">
            <div className="flex items-start justify-between">
              <div className="space-y-2 flex-1">
                <div className="text-lg font-semibold text-white flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-cyan-500/20 border border-cyan-500/30">
                    <Building2 className="w-5 h-5 text-cyan-400" />
                  </div>
                  <span className="bg-gradient-to-r from-cyan-200 to-blue-200 bg-clip-text text-transparent">
                    {companyInfo.name}
                  </span>
                </div>
                <div className="flex flex-wrap gap-4 text-xs">
                  <div>
                    <span className="text-gray-400">CIK:</span>
                    <span className="text-white font-medium ml-1">{companyInfo.cik}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">SIC:</span>
                    <span className="text-white font-medium ml-1">{companyInfo.sic}</span>
                  </div>
                </div>
                {companyInfo.sic_description && (
                  <div className="text-xs text-gray-400 bg-white/5 rounded-lg px-3 py-2 border border-white/10">
                    {companyInfo.sic_description}
                  </div>
                )}
              </div>
            </div>
            
            {/* Addresses */}
            {companyInfo.addresses && (
              <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-white/10">
                {companyInfo.addresses?.mailing && renderAddress(t('edgar.addressMailing'), companyInfo.addresses.mailing)}
                {companyInfo.addresses?.business && renderAddress(t('edgar.addressBusiness'), companyInfo.addresses.business)}
              </div>
            )}
          </div>
        )}

        {/* Form type pills */}
        <div className="flex gap-2 flex-wrap">
          {[
            { key: 'all', label: t('edgar.formLabels.all'), count: formCounts.all, gradient: 'from-blue-500/20 to-cyan-500/20' },
            { key: 'critical', label: t('edgar.formLabels.critical'), count: formCounts.critical, gradient: 'from-rose-500/20 to-red-500/20' },
            { key: 'reports', label: t('edgar.formLabels.reports'), count: formCounts.reports, gradient: 'from-emerald-500/20 to-green-500/20' },
            { key: 'governance', label: t('edgar.formLabels.governance'), count: formCounts.governance, gradient: 'from-amber-500/20 to-yellow-500/20' },
            { key: 'insider', label: t('edgar.formLabels.insider'), count: formCounts.insider, gradient: 'from-purple-500/20 to-violet-500/20' },
            { key: 'registration', label: t('edgar.formLabels.registration'), count: formCounts.registration, gradient: 'from-teal-500/20 to-cyan-500/20' },
            { key: 'other', label: t('edgar.formLabels.other'), count: formCounts.other, gradient: 'from-cyan-500/20 to-blue-500/20' },
          ].filter(({ count }) => count > 0).map(({ key, label, count, gradient }) => (
            <button
              key={key}
              onClick={() => {
                setSelectedForm(key);
                setShowAll(false);
              }}
              className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-300 backdrop-blur-sm border relative overflow-hidden ${
                selectedForm === key
                  ? `bg-gradient-to-r ${gradient} border-white/30 text-white shadow-lg ring-2 ring-white/20`
                  : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10 hover:text-gray-300 hover:border-white/20'
              }`}
            >
              <span className="relative z-10 flex items-center gap-2">
                {label}
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  selectedForm === key ? 'bg-white/20' : 'bg-white/10'
                }`}>
                  {count}
                </span>
              </span>
              {selectedForm === key && (
                <div className={`absolute inset-0 bg-gradient-to-r ${gradient} opacity-20 animate-pulse`} />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-slate-900/20 to-slate-800/20">
          <div className="text-center space-y-6">
            <div className="relative">
              <div className="w-16 h-16 mx-auto border-4 border-slate-700/20 border-t-blue-400 rounded-full animate-spin shadow-lg shadow-blue-400/20" />
              <div className="absolute inset-0 w-16 h-16 mx-auto border-4 border-transparent border-t-cyan-400 rounded-full animate-spin animation-delay-150 shadow-lg shadow-cyan-400/20" />
            </div>
            <div>
              <p className="text-gray-300 text-sm font-medium">{t('edgar.loadingFilings')}</p>
              <p className="text-gray-500 text-xs mt-1">{t('edgar.fetchingDocuments')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {!loading && (
        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-track-slate-800/50 scrollbar-thumb-blue-500/30 hover:scrollbar-thumb-blue-400/50">
          <div className="p-6 space-y-4">
            {filteredData.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 mx-auto bg-gradient-to-br from-slate-700/20 to-slate-600/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6 border border-white/10">
                  <FileText className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-300 mb-3">{t('edgar.noFilingsFound')}</h3>
                <p className="text-gray-500 text-sm max-w-md mx-auto">
                  {selectedForm === 'all'
                    ? t('edgar.noFilingsDefault')
                    : t('edgar.noFilingsFiltered', { category: t(`edgar.formLabels.${selectedForm}`) })}
                </p>
              </div>
            ) : (
              visibleFilings.map((filing, index) => {
                const formConfig = filing.formConfig;
                const key = filing.accession_number || index;
                const isExpanded = expandedFilings[key];
                const bullets = parseSummaryBullets(filing.summary_bullets);
                const hasContent =
                  ['completed'].includes(filing.summary_status) &&
                  bullets.length > 0;


                return (
                  <article
                    key={key}
                    className={`group relative backdrop-blur-sm bg-white/5 rounded-xl border border-white/10 overflow-hidden transition-all duration-500 hover:bg-white/10 hover:border-white/20 hover:shadow-2xl ${formConfig.glowColor} ${
                      hoveredCard === index ? 'scale-[1.02] z-10 ring-2 ring-white/20' : ''
                    }`}
                    onMouseEnter={() => setHoveredCard(index)}
                    onMouseLeave={() => setHoveredCard(null)}
                  >
                    {/* Background gradient */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${formConfig.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                    <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none border border-white/20 shadow-inner" />

                    <div className="relative">
                      {/* Header - Clickable */}
                      <div 
                        className={`p-6 ${hasContent ? 'cursor-pointer' : ''}`}
                        onClick={() => hasContent && toggleFiling(key)}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3 flex-1">
                            <div className={`p-2.5 rounded-xl bg-white/5 backdrop-blur-sm border ${formConfig.borderColor} shadow-lg ${formConfig.glowColor}`}>
                              {formConfig.icon}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`${formConfig.textColor} text-sm font-bold tracking-wider`}>
                                  {filing.form}
                                </span>
                                {hasContent && (
                                  <span className="flex items-center gap-1 text-xs text-gray-400 bg-white/5 px-2 py-0.5 rounded-full border border-white/10">
                                    <Info className="w-3 h-3" />
                                    {t('edgar.summaryPoints', { count: bullets.length })}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3 text-gray-400" />
                                  <span className="text-gray-400 text-xs font-medium">{formatDate(filing.filing_date)}</span>
                                </div>
                                <span className="text-gray-600">•</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${formConfig.textColor} bg-white/10 border border-white/20`}>
                                  {formConfig.label}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <a
                              href={filing.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="opacity-0 group-hover:opacity-100 transition-all duration-300 p-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-blue-400 hover:text-blue-300 backdrop-blur-sm border border-white/10 hover:border-blue-400/30 shadow-lg hover:shadow-blue-400/20"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                            {hasContent && (
                              <div className="p-2 rounded-lg transition-colors">
                                {isExpanded ? (
                                  <ChevronUp className="w-5 h-5 text-gray-400" />
                                ) : (
                                  <ChevronDown className="w-5 h-5 text-gray-400" />
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Description */}
                        {filing.description && (
                          <p className="text-gray-400 text-sm leading-relaxed group-hover:text-gray-300 transition-colors duration-300">
                            {filing.description}
                          </p>
                        )}

                        {/* Preview first bullet when collapsed */}
                        {!isExpanded && hasContent && (
                          <div className="mt-3 pt-3 border-t border-white/10">
                            <div className="flex gap-2 items-start">
                              <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5" />
                              <p className="text-sm text-gray-400 line-clamp-2 flex-1">
                                {bullets[0].replace(/\*\*([^*]+)\*\*/g, '$1')}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Expanded Summary Content */}
                      {isExpanded && hasContent && (
                        <div className="px-6 pb-6 border-t border-white/10 bg-white/5">
                          <div className="pt-4 space-y-4">

                            {/* Section Title */}
                            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                              <Info className="w-3 h-3" />
                              {t('edgar.extractedSegments')}
                            </h4>

                            {/* Proper bullet list */}
                            <ul className="space-y-4">
                              {bullets.map((bullet, idx) => {
                                const cleaned = bullet.replace(/^\-\s*/, "");
                                const parts = cleaned.match(/\*\*([^*]+)\*\*:\s*(.+)/);

                                return (
                                  <li key={idx} className="flex items-start gap-3">
                                    <span className="w-2 h-2 mt-2 block rounded-full bg-blue-400"></span>

                                    <div className="flex-1 space-y-1">
                                      {parts ? (
                                        <>
                                          <div className="text-sm text-blue-300 font-semibold">
                                            {parts[1]}
                                          </div>
                                          <div className="text-sm text-gray-300 leading-relaxed">
                                            {parts[2]}
                                          </div>
                                        </>
                                      ) : (
                                        <p className="text-sm text-gray-300 leading-relaxed">
                                          {cleaned}
                                        </p>
                                      )}
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>


                          </div>
                        </div>
                      )}


                      {/* Footer */}
                      <div className="px-6 pb-6">
                        <div className="flex items-center justify-between pt-4 border-t border-white/10">
                          <div className="flex items-center gap-2 text-gray-500 text-xs">
                            <FileText className="w-3 h-3" />
                            <span className="truncate max-w-48 font-mono">
                              {filing.accession_number}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full text-xs text-blue-400 border border-white/10 shadow-inner font-medium">
                              {t('edgar.secFilingBadge')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })
            )}
          </div>

          {/* Show More / Less */}
          {filteredData.length > 3 && (
            <div className="p-4 border-t border-white/10 bg-gradient-to-r from-white/5 via-white/10 to-transparent">
              <button
                onClick={() => setShowAll((v) => !v)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 hover:from-blue-500/30 hover:to-cyan-500/30 rounded-xl border border-blue-400/30 text-blue-300 hover:text-blue-200 transition-all duration-200"
              >
                <span className="font-medium">
                  {showAll ? t('edgar.showLess') : t('edgar.showMore', { count: filteredData.length - 3 })}
                </span>
                {showAll ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
          )}
          {/* INSERT DISCLAIMER HERE */}
          <div className="p-6 text-[11px] leading-relaxed text-gray-500 border-t border-white/10 bg-slate-900/40 backdrop-blur-sm">
            <p>
              {t('edgar.disclaimer')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default EdgarFilings;